import { EditTemplateCategories } from "../constants/const";
import {
  TEMPLATE_NAME_ENUMS,
  EDIT_TEMPLATE_QUESTION_SCHEMA,
} from "../constants/enums/editTemplateEnums";
import { EditTemplateHelpers } from "./EditTemplateHelpers";

export class EditTemplateDataTransformer {
  static divider = "__";

  static sanitizeMedicineFields(value) {
    if (!value) return value;

    if (Array.isArray(value)) {
      // Check if this array contains medicine objects
      const isMedicineArray = value.length > 0 && value.every(item => 
        item && typeof item === "object" && !Array.isArray(item) &&
        ("Medicine name" in item || "medicine_name" in item || "Drug Class" in item || "drug_class" in item || "Dosage" in item || "dosage" in item)
      );

      if (isMedicineArray) {
        return value.map(item => {
          const newItem = {};
          Object.entries(item).forEach(([k, v]) => {
            newItem[k] = typeof v === "string" ? v.replace(/\+/g, "%2B") : v;
          });
          return newItem;
        });
      }

      return value.map(item => EditTemplateDataTransformer.sanitizeMedicineFields(item));
    }

    if (typeof value === "object" && value !== null) {
      const result = {};
      Object.entries(value).forEach(([k, v]) => {
        if ((k === "custom_medicine" || k === "medicine") && Array.isArray(v)) {
          result[k] = v.map(medItem => {
            if (medItem && typeof medItem === "object" && !Array.isArray(medItem)) {
              const newMedItem = {};
              Object.entries(medItem).forEach(([medKey, medVal]) => {
                newMedItem[medKey] = typeof medVal === "string" ? medVal.replace(/\+/g, "%2B") : medVal;
              });
              return newMedItem;
            }
            return medItem;
          });
        } else {
          result[k] = EditTemplateDataTransformer.sanitizeMedicineFields(v);
        }
      });
      return result;
    }

    return value;
  }

  // Transform form data to api format
  static transform_formData_to_ApiFormat(formData, templateName) {
    if (!TEMPLATE_NAME_ENUMS[templateName]) {
      console.info(`${templateName} is not a valid template name`);
      return;
    }

    const questions = EditTemplateCategories[templateName];

    if (!questions?.length) {
      return [];
    }
    console.info("###questions,formdata", questions, formData);
    // Generate the new template data structure (The Source of Truth for template_data)
    const newTemplateData = this.transformNewTemplateData(questions, formData);

    // Generate raw_data
    const rawDataResponse = [];

    questions.forEach((mainQuestion) => {
      const mainCategoryLabel =
        mainQuestion?.uid || mainQuestion?.category_label;
      const subCategories = mainQuestion?.sub_categories;

      if (!subCategories?.length) return;

      const mainCategoryValues_raw = [];

      subCategories.forEach((subQuestion) => {
        let isDynamic = false;
        let count = 1;

        if (subQuestion.isdynamic && subQuestion.dynamicValue) {
          let targetKey = null;
          for (const key of Object.keys(formData)) {
            if (
              key.toLowerCase().includes(subQuestion.dynamicValue.toLowerCase())
            ) {
              targetKey = key;
              break;
            }
          }
          const nValue = targetKey
            ? formData[targetKey]
            : formData[subQuestion.dynamicValue];
          const parsedCount = parseInt(nValue, 10);
          if (!isNaN(parsedCount) && parsedCount > 0) {
            isDynamic = true;
            count = parsedCount;
          } else {
            count = 0; // Don't render if it's dynamic but count is 0
          }
        }

        for (let i = 1; i <= count; i++) {
          let currentSubQuestion = subQuestion;

          if (isDynamic) {
            // Formulate basePrefix e.g. "Past Obstetric History_0"
            const basePrefix = `${mainCategoryLabel}_${i - 1}`;

            // Adjust label for UI/raw_data consistency mapping
            const baseName =
              (subQuestion.category_label || subQuestion.generic_data_name)
                ?.replace("n", "")
                .replace(/-/g, "")
                .trim() || "Child";
            const resolvedLabel = `${baseName} - ${i}`;

            currentSubQuestion = EditTemplateHelpers.assignDynamicTabIds(
              subQuestion,
              basePrefix,
              resolvedLabel
            );
            currentSubQuestion.category_label = resolvedLabel;
          }

          const subCategoryLabel = isDynamic
            ? currentSubQuestion.category_label
            : currentSubQuestion.uid || currentSubQuestion.category_label;
          const childQuestions = currentSubQuestion.questions;

          if (!childQuestions?.length) continue;

          const subCategoryValues_raw = [];

          childQuestions.forEach((childQuestion) => {
            this.transformFinalQuestionDataToApiFormat(
              childQuestion,
              formData,
              subCategoryValues_raw
            );
          });

          //Push to raw data set
          if (subCategoryValues_raw.length > 0) {
            mainCategoryValues_raw.push({
              [subCategoryLabel]: subCategoryValues_raw,
            });
          }
        }
      });

      //Push to main category raw data
      if (mainCategoryValues_raw.length > 0) {
        rawDataResponse.push({ [mainCategoryLabel]: mainCategoryValues_raw });
      }
    });

    // AGGREGATE CDSS DATA ON SAVE
    const allMedical = new Set();
    const allFamily = new Set();
    const allRisk = new Set();

    // Check if initial CDSS data was passed (preserved from API load)
    (formData.ccdss_medical_history || []).forEach((v) => allMedical.add(v));
    (formData.ccdss_familty_history || []).forEach((v) => allFamily.add(v));
    (formData.ccdss_risk_factors || []).forEach((v) => allRisk.add(v));

    // Merge in all question-level CDSS metadata
    Object.keys(formData).forEach((key) => {
      if (key.startsWith("_cdss_medical_history__")) {
        (formData[key] || []).forEach((v) => allMedical.add(v));
      } else if (key.startsWith("_cdss_familty_history__")) {
        (formData[key] || []).forEach((v) => allFamily.add(v));
      } else if (key.startsWith("_cdss_risk_factors__")) {
        (formData[key] || []).forEach((v) => allRisk.add(v));
      }
    });

    // Sanitize medicine fields by replacing '+' with '%2B' to avoid URL-decoding issues on backend
    const sanitizedRawData = this.sanitizeMedicineFields(rawDataResponse);
    const sanitizedTemplateData = this.sanitizeMedicineFields(newTemplateData);

    return {
      raw_data: sanitizedRawData || [],
      template_data: sanitizedTemplateData || [],
      ccdss_medical_history: Array.from(allMedical),
      ccdss_familty_history: Array.from(allFamily),
      ccdss_risk_factors: Array.from(allRisk),
    };
  }

  // Transform final question data to API format (Populates raw_data only now)
  static transformFinalQuestionDataToApiFormat(
    childQuestion,
    formData,
    subCategoryValues_raw
  ) {
    if (childQuestion?.have_2_option) {
      const nestedResult = this.transformNestedFormQuestionToRaw(
        childQuestion,
        formData
      );
      if (nestedResult && nestedResult.length > 0) {
        const fieldName = childQuestion?.uid || childQuestion?.label?.trim();
        subCategoryValues_raw.push({ [fieldName]: nestedResult });
      }
      return;
    }
    const fieldName = childQuestion?.uid || childQuestion?.label?.trim(); // The Category Label (e.g. PHQ 9)
    const questionKey = childQuestion?.name;
    let value = formData[questionKey];
    value = EditTemplateHelpers.validateValue(childQuestion, value);

    const isLikert =
      childQuestion?.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.LIKERT;
    const isSelectionWithQuestions =
      childQuestion?.schema ===
        EDIT_TEMPLATE_QUESTION_SCHEMA.SELECTION_WITH_QUESTIONS ||
      childQuestion?.schema ===
        EDIT_TEMPLATE_QUESTION_SCHEMA.MULTIPLE_COMPONENT_DATA ||
      childQuestion?.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.MULTIPLE_FIELDS ||
      childQuestion?.sub_questions?.some(
        (sq) =>
          sq.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.SELECTION_WITH_QUESTIONS
      );

    // --- LIKERT HANDLING ---
    if (isLikert) {
      if (
        childQuestion.likert_data &&
        childQuestion.likert_options &&
        Array.isArray(childQuestion.likert_data)
      ) {
        const likertResult = [];
        // LikertTable stores data as an object under the main question key
        // Keys inside are formatted as: `${questionName}_q${index + 1}`
        const likertValues = value || {};

        childQuestion.likert_data.forEach((_, index) => {
          // Construct the key used by LikertTable
          const lookupKey = `${childQuestion.name}_q${index + 1}`;

          let rowValue = likertValues[lookupKey];
          // Use the question text as the label/key in the output, NOT the option values
          const rowLabel =
            childQuestion.likert_data[index]?.uid ||
            childQuestion.likert_data[index]?.question;

          // Defensive Fix: Ensure value matches schema format (e.g. restore "+1" from "1")
          if (
            rowValue !== undefined &&
            rowValue !== null &&
            rowValue !== "" &&
            childQuestion.likert_options
          ) {
            const matchedOption = childQuestion.likert_options.find((opt) => {
              const optStr = String(opt);
              const valStr = String(rowValue);
              if (optStr === valStr) return true;
              if (optStr === `+${valStr}`) return true;
              if (
                parseInt(optStr) === parseInt(valStr) &&
                !isNaN(parseInt(valStr))
              )
                return true;
              return false;
            });
            if (matchedOption) rowValue = matchedOption;
          }

          if (rowValue !== undefined && rowValue !== null && rowValue !== "") {
            likertResult.push({ [rowLabel]: rowValue });
          }
        });

        if (likertResult.length > 0) {
          subCategoryValues_raw.push({ [fieldName]: likertResult });
        }
      }
      return;
    }

    // --- SELECTION WITH QUESTIONS HANDLING ---
    // Updated logic in EditTemplateDataTransformer.js
    if (isSelectionWithQuestions) {
      const relatedQuestions = EditTemplateHelpers.getRelatedQuestionValues(
        childQuestion,
        formData,
        "", // Force flat array extraction
        !childQuestion?.have_option // useUid
      );

      let relatedData = relatedQuestions;

      // Defensive Fix: Convert object to API-style array if needed
      if (
        !Array.isArray(relatedData) &&
        typeof relatedData === "object" &&
        relatedData !== null
      ) {
        relatedData = Object.entries(relatedData).map(([key, val]) => ({
          [key]: Array.isArray(val) ? val : [val],
        }));
      }

      // Map keys to UIDs if possible (Deep Mapping)
      if (Array.isArray(relatedData)) {
        relatedData = relatedData.map((item) => {
          const key = Object.keys(item)[0]; // e.g. "EENT..._0"
          let val = item[key]; // e.g. [{ "Inconclusive": ... }]

          if (Array.isArray(val)) {
            val = val.map((nestedItem) => {
              if (
                typeof nestedItem === "object" &&
                nestedItem !== null &&
                !Array.isArray(nestedItem)
              ) {
                const nestedKey = Object.keys(nestedItem)[0]; // e.g. "Inconclusive"
                const nestedVal = nestedItem[nestedKey];

                // Find the parent component (EENT..._0) in childQuestion
                const parentComp =
                  childQuestion.component_data?.find(
                    (c) => c.uid === key || c.name === key
                  ) ||
                  childQuestion.sub_questions?.find(
                    (sq) => sq.uid === key || sq.name === key
                  );

                if (parentComp) {
                  // Look for the nested option (Inconclusive) in the parent's data
                  const targetSource =
                    parentComp.component_data || parentComp.sub_data;
                  const targetOption = targetSource?.find(
                    (t) => t.name === nestedKey || t.uid === nestedKey
                  );

                  if (targetOption?.uid) {
                    return { [targetOption.uid]: nestedVal };
                  }
                }
              }
              return nestedItem;
            });
          }
          return { [key]: val };
        });
      }

      if (Array.isArray(relatedData) && relatedData.length > 0) {
        subCategoryValues_raw.push({ [fieldName]: relatedData });
      }
      return;
    }

    // --- EXISTING LOGIC FOR OTHER TYPES (Preserved but simplified indentation where possible) ---
    const isMultiple = childQuestion?.multiple_selection;
    const isContainer =
      childQuestion?.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.MULTIPLE_FIELDS ||
      childQuestion?.schema ===
        EDIT_TEMPLATE_QUESTION_SCHEMA.MULTIPLE_COMPONENT_DATA ||
      childQuestion?.schema ===
        EDIT_TEMPLATE_QUESTION_SCHEMA.SELECTION_WITH_QUESTIONS || // Ensure it's treated as a container
      (childQuestion?.component_data?.length > 0 &&
        (value === undefined || value === null || value === ""));
    // Only process if a value exists or it's a container
    if (
      (value !== undefined &&
        value !== null &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0)) ||
      isContainer
    ) {
      let parentValueForLookup = value || "";
      if (childQuestion?.have_option && value) {
        const valArray = Array.isArray(value) ? value : [value];
        const mappedValues = valArray.map((v) => {
          const opt =
            childQuestion.component_data?.find(
              (c) => c.uid === v || c.name === v
            ) ||
            childQuestion.options?.find(
              (o) => o.uid === v || o.value === v || o.label === v
            );
          return opt ? opt.name || opt.label : v;
        });
        parentValueForLookup = mappedValues;
      }

      const relatedQuestions = EditTemplateHelpers.getRelatedQuestionValues(
        childQuestion,
        formData,
        parentValueForLookup,
        !childQuestion?.have_option // useUid
      );
      console.log(childQuestion.label, "relatedQuestions:", relatedQuestions);

      // Helper for checking sub-questions
      const hasSubQuestions = (item) => {
        if (
          childQuestion.schema ===
            EDIT_TEMPLATE_QUESTION_SCHEMA.SELECTION_WITH_QUESTIONS ||
          childQuestion.schema ===
            EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_MULTILINE ||
          childQuestion.schema ===
            EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_SINGLE_LINE ||
          childQuestion.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.DATE_PICKER
        )
          return false;

        const valueToCheck = Array.isArray(item) ? item[0] : item;
        return childQuestion.sub_questions?.some((sq) => {
          const isConditional =
            sq.conditional_parrent_values?.includes(valueToCheck);
          const isUnconditional = !sq.conditional_parrent_values?.length;

          return isConditional || isUnconditional;
        });
      };

      if (isContainer) {
        const hasData =
          relatedQuestions &&
          Object.keys(relatedQuestions).length > 0 &&
          Object.values(relatedQuestions).some(
            (val) =>
              val !== null &&
              val !== undefined &&
              val !== "" &&
              !(Array.isArray(val) && val.length === 0)
          );

        const alreadyAdded = subCategoryValues_raw.some(
          (item) => Object.keys(item)[0] === fieldName
        );

        // if (!alreadyAdded && hasData) {
        //   subCategoryValues_raw.push({
        //     [fieldName]: ["Yes"],
        //   });
        // }
      }

      if (
        (relatedQuestions && Object.keys(relatedQuestions).length > 0) ||
        (childQuestion.have_option && isMultiple)
      ) {
        if (isMultiple) {
          const filteredValue = Array.isArray(value)
            ? value.filter((item) => {
                const hasSub = hasSubQuestions(item);
                // console.log("###hasSub", item, hasSub, relatedQuestions[item]);
                return !hasSub || relatedQuestions[item] !== undefined;
              })
            : [];

          if (filteredValue.length === 0) return;

          const mappedValues = filteredValue
            .map((item) => {
              // Normalize item: if it's an object { UID: "value" }, extract the key.
              const itemNormKey =
                typeof item === "object" &&
                item !== null &&
                !Array.isArray(item)
                  ? Object.keys(item)[0]
                  : item;

              let data = relatedQuestions[itemNormKey] || {};

              const opt =
                childQuestion.options?.find(
                  (o) =>
                    (o.name || o.label) === itemNormKey ||
                    o.uid === itemNormKey ||
                    o.value === itemNormKey
                ) ||
                childQuestion.component_data?.find(
                  (c) => c.name === itemNormKey || c.uid === itemNormKey
                );

              // Fallback: If no related/sub-question data found, check if this option
              // itself holds data in formData (Leaf node pattern like 'Site' -> 'Foot')
              if (Object.keys(data).length === 0 && opt?.uid && formData) {
                const leafKey = EditTemplateHelpers.getQuestionKey(
                  questionKey,
                  opt.uid
                );
                const leafValue = formData[leafKey];

                if (
                  leafValue !== undefined &&
                  leafValue !== null &&
                  leafValue !== "" &&
                  !(Array.isArray(leafValue) && leafValue.length === 0)
                ) {
                  data = Array.isArray(leafValue) ? leafValue : [leafValue];
                }

                // Check for input_field specific fallback
                if (opt?.input_field) {
                  const inputFieldKey = `${questionKey}__${EditTemplateHelpers.normalizeUID(opt.uid)}__${EditTemplateHelpers.normalizeUID(opt.input_field?.uid)}`;
                  const inputFieldValue = formData[inputFieldKey];

                  if (
                    inputFieldValue !== undefined &&
                    inputFieldValue !== null &&
                    inputFieldValue !== ""
                  ) {
                    data = inputFieldValue; // This will return { [itemKey]: "123" } at the end
                  }
                }
              }

              // Filter out "Yes" if it has no data (App format compatibility)
              if (item === "Yes" && Object.keys(data).length === 0) {
                return null;
              }

              // Map Name back to UID if needed for output key
              let itemKey = itemNormKey;
              if (childQuestion.have_option) {
                if (opt?.uid) itemKey = opt.uid;
              }

              // If the original item was an object and we found no deep sub-data,
              // or if this option has an input field, we might want to return the object structure itself.
              if (
                typeof item === "object" &&
                item !== null &&
                !Array.isArray(item) &&
                Object.keys(data).length === 0
              ) {
                return item;
              }

              return {
                [itemKey]: data,
              };
            })
            .filter((item) => item !== null);
          if (mappedValues?.length) {
            subCategoryValues_raw.push({ [fieldName]: mappedValues });
          }
        } else {
          // Single selection
          if (
            hasSubQuestions(value) &&
            Object.keys(relatedQuestions).length === 0
          )
            return;

          if (isContainer) {
            if (
              childQuestion.schema ===
              EDIT_TEMPLATE_QUESTION_SCHEMA.MULTIPLE_COMPONENT_DATA
            ) {
              const processContainerValue = (sqKey, sqVal) => {
                const subQ = childQuestion.sub_questions?.find(
                  (q) => q.name === sqKey || q.uid === sqKey
                );
                if (!subQ || !Array.isArray(sqVal)) {
                  if (Array.isArray(sqVal)) return sqVal;
                  if (typeof sqVal === "object" && sqVal !== null) return sqVal;
                  return [sqVal];
                }

                return sqVal.map((v) => {
                  let lookupVal = v;
                  let parsedDirectValue = null;

                  if (
                    typeof v === "object" &&
                    v !== null &&
                    !Array.isArray(v)
                  ) {
                    lookupVal = Object.keys(v)[0];
                    parsedDirectValue = v[lookupVal];
                  }

                  const opt =
                    subQ.options?.find(
                      (o) =>
                        o.value === lookupVal ||
                        o.label === lookupVal ||
                        o.name === lookupVal ||
                        o.uid === lookupVal
                    ) ||
                    subQ.component_data?.find(
                      (o) =>
                        o.value === lookupVal ||
                        o.label === lookupVal ||
                        o.name === lookupVal ||
                        o.uid === lookupVal
                    );
                  if (opt?.input_field && opt?.uid && formData) {
                    // Try to get value from formData first (like before)
                    const inputFieldKey = `${subQ.name}__${EditTemplateHelpers.normalizeUID(opt.uid)}__${EditTemplateHelpers.normalizeUID(opt.input_field?.uid)}`;
                    let inputFieldValue = formData[inputFieldKey];

                    // If not in formData but we had it in the object (parsedDirectValue), use that
                    if (
                      (inputFieldValue === undefined ||
                        inputFieldValue === null ||
                        inputFieldValue === "") &&
                      parsedDirectValue !== null &&
                      parsedDirectValue !== undefined
                    ) {
                      inputFieldValue = parsedDirectValue;
                    }

                    // Always emit the selected option even if the input field is empty.
                    // This is crucial when the tab was never opened, because the
                    // input field never registered an empty string into the formData.
                    return {
                      __directEmit: true,
                      [opt.uid]: inputFieldValue || "",
                    };
                  }
                  return v;
                });
              };

              // 👉 Convert object to array of single-key objects (API format)
              const containerValuesArray = Array.isArray(relatedQuestions)
                ? relatedQuestions
                : Object.entries(relatedQuestions).flatMap(([key, val]) => {
                    const processedVal = processContainerValue(key, val);

                    if (Array.isArray(processedVal)) {
                      const directItems = processedVal
                        .filter(
                          (item) =>
                            typeof item === "object" &&
                            item !== null &&
                            item.__directEmit
                        )
                        .map(({ __directEmit, ...rest }) => rest); // strip the sentinel

                      const regularItems = processedVal.filter(
                        (item) =>
                          !(
                            typeof item === "object" &&
                            item !== null &&
                            item.__directEmit
                          )
                      );

                      const results = [];
                      // Emit regular selections as { [sqKey]: [regularItems] }
                      if (regularItems.length > 0) {
                        results.push({ [key]: regularItems });
                      }
                      // Emit each input_field option directly as { [opt.uid]: value }
                      directItems.forEach((item) => results.push(item));
                      return results;
                    }
                    return [{ [key]: processedVal }];
                  });

              subCategoryValues_raw.push({
                [fieldName]: containerValuesArray,
              });
            }
          } else {
            console.log("relatedQuestions: else", relatedQuestions); // debug

            // Map keys in relatedQuestions to UIDs if possible (e.g. "Yes" -> "General_0_6_0")
            let finalRelatedQuestions = relatedQuestions;
            if (
              relatedQuestions &&
              typeof relatedQuestions === "object" &&
              !Array.isArray(relatedQuestions)
            ) {
              const newRelatedQuestions = {};
              Object.keys(relatedQuestions).forEach((key) => {
                let newKey = key;
                // Find option by Name
                const opt =
                  childQuestion.component_data?.find((c) => c.name === key) ||
                  childQuestion.options?.find(
                    (o) => (o.name || o.label) === key
                  );
                if (opt?.uid) {
                  newKey = opt.uid;
                }
                newRelatedQuestions[newKey] = relatedQuestions[key];
              });
              finalRelatedQuestions = newRelatedQuestions;
            }

            subCategoryValues_raw.push({
              [fieldName]: [finalRelatedQuestions],
            });
          }
        }
      } else {
        // No related questions answered
        if (isContainer) return;

        const inputComponent = childQuestion?.component_data?.find(
          (c) =>
            c.type === "input_single_line" ||
            c.type === "input_single_line_with_title" ||
            c.type === "input_multiline" ||
            c.type === "input_multiline_with_title" ||
            c.type === "date_picker" ||
            c.type === "drop_down" ||
            c.type === "drop_down_without_title" ||
            c.type === "drop_down_with_title" ||
            c.type === "drop_down_with_title_option"
        );

        if (inputComponent && inputComponent.uid) {
          // If value is missing at parent level, check if it exists at the nested key
          let inputValue = value;
          if (!inputValue) {
            const nestedKey = `${questionKey}_${inputComponent.uid}`;
            inputValue = formData[nestedKey];
          }

          if (
            inputValue !== undefined &&
            inputValue !== null &&
            inputValue !== ""
          ) {
            subCategoryValues_raw.push({
              [fieldName]: {
                [inputComponent.uid]: Array.isArray(inputValue)
                  ? inputValue
                  : [inputValue],
              },
            });
          }
          return;
        }

        if (isMultiple) {
          const filteredValue = Array.isArray(value)
            ? value.filter((item) => !hasSubQuestions(item))
            : [];
          if (filteredValue.length > 0) {
            const mappedValues = filteredValue.map((item) => {
              const opt =
                childQuestion.options?.find(
                  (o) => o.value === item || o.label === item || o.name === item
                ) ||
                childQuestion.component_data?.find(
                  (c) =>
                    c.value === item ||
                    c.label === item ||
                    c.name === item ||
                    c.uid === item
                );

              if (opt?.input_field && opt?.uid && formData) {
                const inputFieldKey = `${questionKey}__${EditTemplateHelpers.normalizeUID(opt.uid)}__${EditTemplateHelpers.normalizeUID(opt.input_field?.uid)}`;
                const inputFieldValue = formData[inputFieldKey];

                if (
                  inputFieldValue !== undefined &&
                  inputFieldValue !== null &&
                  inputFieldValue !== ""
                ) {
                  return { [opt.uid]: inputFieldValue };
                }
              }
              // If a simple selection doesn't have an input field answered, it retains string format
              // Use UID if available for raw data formatting
              return childQuestion.have_option && opt?.uid ? opt.uid : item;
            });
            subCategoryValues_raw.push({ [fieldName]: mappedValues });
          }
        } else {
          // Single selection
          if (!hasSubQuestions(value)) {
            const opt =
              childQuestion.options?.find(
                (o) =>
                  o.value === value || o.label === value || o.name === value
              ) ||
              childQuestion.component_data?.find(
                (c) =>
                  c.value === value ||
                  c.label === value ||
                  c.name === value ||
                  c.uid === value
              );

            let finalOutput = Array.isArray(value) ? value : [value];

            if (opt?.input_field && opt?.uid && formData) {
              const inputFieldKey = `${questionKey}__${EditTemplateHelpers.normalizeUID(opt.uid)}__${EditTemplateHelpers.normalizeUID(opt.input_field?.uid)}`;
              const inputFieldValue = formData[inputFieldKey];
              if (
                inputFieldValue !== undefined &&
                inputFieldValue !== null &&
                inputFieldValue !== ""
              ) {
                finalOutput = [{ [opt.uid]: inputFieldValue }];
              }
            }

            subCategoryValues_raw.push({
              [fieldName]: finalOutput,
            });
          }
        }
      }
    }
  }

  static transformNewTemplateData(questions, formData) {
    console.log("questions - ", questions);
    const normalizeLabel = (txt) => {
      if (txt === null || txt === undefined) return txt;
      return String(txt)
        .replace(/\s*\n\s*/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    };

    const appendUnitIfNeeded = (question, value) => {
      if (!value) return value;
      if (!question?.unit) return value;

      // Prevent double unit (e.g. "95 %" again)
      const valueStr = String(value).trim();
      if (
        valueStr.endsWith(` ${question.unit}`) ||
        valueStr.endsWith(question.unit)
      ) {
        return valueStr;
      }

      return `${valueStr} ${question.unit}`;
    };

    let result = [];

    const processQuestionsList = (questionList) => {
      let items = [];
      const processQuestion = (question) => {
        if (!question) return;

        // --- LIKERT HANDLING (PHQ, GAD, etc.) ---
        if (question.schema === "likert") {
          const likertAnswers = formData[question.name];

          if (
            !likertAnswers ||
            typeof likertAnswers !== "object" ||
            Object.keys(likertAnswers).length === 0
          ) {
            return;
          }

          items.push({
            item_name: question.label || "",
            value: "Yes",
            required_for_cdss: true,
            cdss_parameter: question.cdss_parameter || null,
          });

          question.likert_data.forEach((row, index) => {
            const answerKey = `${question.name}_q${index + 1}`;
            const answerValue = likertAnswers[answerKey];

            if (
              answerValue !== null &&
              answerValue !== undefined &&
              answerValue !== ""
            ) {
              items.push({
                item_name: "•" + row.question,
                value: String(answerValue),
                required_for_cdss: true,
                cdss_parameter: row.cdss_parameter || null,
              });
            }
          });

          return;
        }

        const fieldName = question.label || question.item_name || question.name;
        let value = formData[question.name];

        if (EditTemplateHelpers.validateValue) {
          value = EditTemplateHelpers.validateValue(question, value);
        }

        const relatedQuestions =
          question.schema === "multiple_fields" ||
          question.schema === "multiple_component_data" ||
          question.schema === "selection_with_questions"
            ? EditTemplateHelpers.getSelectionWithQuestionsRelatedValues(
                question,
                formData
              )
            : EditTemplateHelpers.getRelatedQuestionValues(
                question,
                formData,
                value || ""
              );

        console.log(
          "related question of subQ -",
          question.schema,
          fieldName,
          relatedQuestions
        );

        if (
          question.schema === "radio" &&
          Array.isArray(question.sub_questions) &&
          question.sub_questions.length === 1
        ) {
          console.log("farz!@#---1", question);
          const childQ = question.sub_questions[0];

          const isChildInput =
            childQ?.schema === "input_single_line" ||
            childQ?.schema === "input_multiline";

          if (isChildInput) {
            let childVal = formData[childQ.name];
            if (EditTemplateHelpers.validateValue) {
              childVal = EditTemplateHelpers.validateValue(childQ, childVal);
            }

            const hasChildVal =
              childVal !== undefined &&
              childVal !== null &&
              childVal !== "" &&
              !(Array.isArray(childVal) && childVal.length === 0);

            if (hasChildVal) {
              let finalVal = Array.isArray(childVal)
                ? childVal.filter(Boolean).map(String).join(", ")
                : String(childVal);

              // append unit from CHILD question
              finalVal = appendUnitIfNeeded(childQ, finalVal);

              const getLabel = (valStr) => {
                const opt = [
                  ...(question.options || []),
                  ...(question.component_data || []),
                ].find(
                  (o) =>
                    o.uid === valStr ||
                    o.value === valStr ||
                    o.name === valStr ||
                    o.label === valStr
                );
                return (
                  opt?.name || opt?.label || opt?.title || opt?.hint || valStr
                );
              };

              let parentValArray = Array.isArray(value) ? value : [value];
              parentValArray = parentValArray
                .filter(Boolean)
                .map((v) => getLabel(String(v)));

              // Remove raw UIDs if there are other valid human-readable labels
              const isUid = (str) => /(_\d+){2,}$/.test(str);
              const readableLabels = parentValArray.filter((v) => !isUid(v));

              let finalParentVal = "";
              if (readableLabels.length > 0) {
                finalParentVal = Array.from(new Set(readableLabels)).join(", ");
              } else {
                finalParentVal = Array.from(new Set(parentValArray)).join(", ");
              }

              items.push({
                item_name: fieldName,
                value: finalParentVal + " - " + finalVal,
                ...(question.required_for_cdss && { required_for_cdss: true }),
                cdss_parameter: question.cdss_parameter || null,
              });

              // Stop here so parent "Yes" is NOT added and child is NOT flattened separately
              return;
            }
          }
        }

        //eg: Initial Hearing Assessment
        if (Array.isArray(relatedQuestions) && relatedQuestions.length > 0) {
          // 1) Add parent row (same behavior as other complex parents)
          const alreadyHasParent = items.some((i) => i.item_name === fieldName);
          if (!alreadyHasParent) {
            items.push({
              item_name: fieldName,
              value: "Yes",
              ...(question.required_for_cdss && { required_for_cdss: true }),
              cdss_parameter: question.cdss_parameter || null,
            });
          }

          // 2) Add child rows
          relatedQuestions.forEach((row) => {
            let rowValue = String(row.value);

            // Append unit if sub question is an input field
            const subQSchema = question.sub_questions?.find(
              (sq) => sq.label === row.item_name || sq.name === row.item_name
            );

            if (
              subQSchema &&
              (subQSchema.schema === "input_single_line" ||
                subQSchema.schema === "input_multiline")
            ) {
              rowValue = appendUnitIfNeeded(subQSchema, rowValue);
            }

            items.push({
              item_name: row.item_name,
              value: rowValue,
              ...(row.required_for_cdss && { required_for_cdss: true }),
              cdss_parameter: row.cdss_parameter || null,
            });
          });

          // IMPORTANT: stop here so old object-based flattening does not run
          return;
        }

        let selectedOptionInfo = null;

        if (
          question.schema === "select" &&
          Array.isArray(question.options) &&
          value &&
          (typeof value === "string" || Array.isArray(value))
        ) {
          const selectedValue = Array.isArray(value) ? value[0] : value;

          const selectedOption = question.options.find(
            (opt) => opt.value === selectedValue || opt.label === selectedValue
          );

          if (selectedOption?.info) {
            selectedOptionInfo = selectedOption.info;
          }
        }

        // Medicine Logic
        if (question.isMedicine) {
          if (relatedQuestions && Object.keys(relatedQuestions).length > 0) {
            const firstKey = Object.keys(relatedQuestions)[0];
            let medicineData = relatedQuestions[firstKey];
            if (
              typeof medicineData === "object" &&
              !Array.isArray(medicineData) &&
              medicineData !== null
            ) {
              const values = Object.values(medicineData);
              const arrayVal = values.find((v) => Array.isArray(v));
              if (arrayVal) medicineData = arrayVal;
            }
            if (Array.isArray(medicineData)) {
              // Distinguish between standard and custom medicine schemas
              const medicineSubQ = question.sub_questions?.find(
                (sq) =>
                  sq.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_MEDICINE ||
                  sq.schema ===
                    EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_CUSTOM_MEDICINE
              );

              const isCustom =
                medicineSubQ?.schema ===
                EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_CUSTOM_MEDICINE;

              items.push({
                item_name: fieldName,
                [isCustom ? "custom_medicine" : "medicine"]: medicineData,
                required_for_cdss: !!question.required_for_cdss,
                cdss_parameter: question.cdss_parameter || null,
                value: String(value || "Yes"),
              });
              return;
            }
          }
        }

        const hasValue =
          value !== undefined &&
          value !== null &&
          value !== "" &&
          !(Array.isArray(value) && value.length === 0);

        const isSelectionImageQuestion =
          question.have_option &&
          question.multiple_selection &&
          question.enable_selection_image;

        if ((question.have_2_option || isSelectionImageQuestion) && hasValue) {
          const finalStrArray =
            EditTemplateDataTransformer.formatNestedTemplateData(
              question,
              formData,
              isSelectionImageQuestion
            );
          if (finalStrArray && finalStrArray.length > 0) {
            const existingItem = items.find((i) => i.item_name === fieldName);
            if (existingItem) {
              if (question.required_for_cdss)
                existingItem.required_for_cdss = true;
              if (question.cdss_parameter)
                existingItem.cdss_parameter = question.cdss_parameter;
            } else {
              items.push({
                item_name: fieldName,
                value: finalStrArray,
                ...(question.required_for_cdss && { required_for_cdss: true }),
                cdss_parameter: question.cdss_parameter || null,
              });
            }
          }
          return;
        }

        const hasDropDownWithTitleOption = question.sub_questions?.some(
          (sq) => sq?.schema === "drop_down_with_title_option"
        );
        const isDropDownWithTitleOptionParent =
          question.have_option && hasDropDownWithTitleOption;

        let useAggregation = false;

        if (
          question.schema === "multiple_component_data" ||
          question.schema === "multiple_fields"
        ) {
          useAggregation = false;
        } else if (question.have_2_option) {
          useAggregation = true;
        } else if (question.have_option) {
          if (
            question.schema === "radio" &&
            question.multiple_selection &&
            question.capture_options
          ) {
            useAggregation = false;
          } else if (hasDropDownWithTitleOption) {
            useAggregation = false;
          }
          // Check for specific sub_data_multiple_selection pattern (User Request)
          else if (
            question.component_data?.some((c) => c.name === "Yes") &&
            question.sub_data_multiple_selection
          ) {
            useAggregation = true;
          }
          // Check if relatedQuestions implies complex distinct questions
          // If relatedQuestions[value] is an Object with multiple keys, use Flattening.
          else if (hasValue && relatedQuestions && relatedQuestions[value]) {
            const childData = relatedQuestions[value];
            const isComplexObject =
              typeof childData === "object" &&
              childData !== null &&
              !Array.isArray(childData) &&
              Object.keys(childData).length > 1;
            useAggregation = !isComplexObject;
          } else {
            useAggregation = true; // Default to aggregation if no complex structure found
          }
        }

        if (useAggregation) {
          let finalValueString = "";

          if (
            value === "No" ||
            (Array.isArray(value) && value.includes("No"))
          ) {
            finalValueString = "No";
          } else if (
            relatedQuestions &&
            Object.keys(relatedQuestions).length > 0
          ) {
            const extractLeafValues = (obj) => {
              let leaves = [];
              if (
                typeof obj === "object" &&
                obj !== null &&
                !Array.isArray(obj)
              ) {
                Object.entries(obj).forEach(([k, v]) => {
                  // Check for nested object recursion
                  if (
                    typeof v === "object" &&
                    v !== null &&
                    !Array.isArray(v)
                  ) {
                    leaves.push(...extractLeafValues(v));
                  } else if (Array.isArray(v)) {
                    v.forEach((val) => {
                      // CASE 1: array contains objects → recurse (IMPORTANT)
                      if (typeof val === "object" && val !== null) {
                        Object.entries(val).forEach(([objKey, objVal]) => {
                          if (
                            objVal !== undefined &&
                            objVal !== null &&
                            String(objVal).trim() !== ""
                          ) {
                            // ✅ FIX: find the component matching the outer key (k = "General_0_6_0")
                            // then look up objKey in that component's sub_data
                            const parentComponent =
                              question.component_data?.find((c) => c.uid === k);
                            const subData =
                              parentComponent?.sub_data ||
                              question.component_data?.[0]?.sub_data;
                            const matchedOption = subData?.find(
                              (opt) => opt.uid === objKey
                            );
                            const displayKey =
                              matchedOption?.name ||
                              matchedOption?.input_field?.hint ||
                              objKey;

                            if (matchedOption?.input_field) {
                              // "Others" with typed text → "Others : jhjhjh"
                              leaves.push(`${displayKey} : ${String(objVal)}`);
                            } else {
                              // Normal checkbox selection → just the name
                              leaves.push(displayKey);
                            }
                          }
                        });
                      }
                      // CASE 2: array contains primitive values
                      else if (
                        val !== undefined &&
                        val !== null &&
                        String(val).trim() !== ""
                      ) {
                        leaves.push(String(val));
                      }
                    });
                  } else {
                    // Primitive value
                    if (
                      v === null ||
                      v === undefined ||
                      String(v).trim() === ""
                    )
                      return;

                    if (v === "Yes") {
                      leaves.push(k);
                    } else {
                      if (
                        k === "Yes" ||
                        k === "undefined" ||
                        /^[A-Za-z0-9_]+(_\d+)+$/.test(k)
                      ) {
                        leaves.push(String(v));
                      } else {
                        const subQ = question.sub_questions?.find(
                          (sq) =>
                            sq.label?.trim() === k || sq.name?.trim() === k
                        );

                        if (
                          question.have_option &&
                          relatedQuestions &&
                          Object.keys(relatedQuestions).includes("Yes")
                        ) {
                          leaves.push(String(v));
                        } else {
                          const separator =
                            subQ?.schema === "drop_down_with_title"
                              ? "  : "
                              : " - ";
                          leaves.push(`${k}${separator}${String(v)}`);
                        }
                      }
                    }
                  }
                });
              } else if (Array.isArray(obj)) {
                obj.forEach((val) => {
                  // Only add primitive values
                  if (
                    (typeof val !== "object" || val === null) &&
                    val !== undefined &&
                    val !== null &&
                    String(val).trim() !== ""
                  ) {
                    leaves.push(String(val));
                  }
                });
              } else if (
                obj !== null &&
                obj !== undefined &&
                String(obj).trim() !== ""
              ) {
                leaves.push(String(obj));
              }
              return leaves;
            };

            const subData = question.component_data?.[0]?.sub_data;

            // Check if relatedQuestions is { Yes: [{uid: val}, ...] } — component_data pattern
            const yesValue = relatedQuestions?.["Yes"];
            const hasSubInputField =
              Array.isArray(yesValue) &&
              yesValue.some((obj) => {
                const uid = Object.keys(obj)[0];
                return subData?.find((s) => s.uid === uid)?.input_field;
              });

            if (
              !hasSubInputField &&
              Array.isArray(yesValue) &&
              yesValue.every(
                (item) => typeof item === "object" && !Array.isArray(item)
              )
            ) {
              // Push parent as "Yes"
              items.push({
                item_name: fieldName,
                value: "Yes",
                ...(question.required_for_cdss && { required_for_cdss: true }),
                cdss_parameter: question.cdss_parameter || null,
              });

              // Push each sub item separately using hint as label
              yesValue.forEach((obj) => {
                Object.entries(obj).forEach(([uid, val]) => {
                  if (
                    val !== undefined &&
                    val !== null &&
                    String(val).trim() !== ""
                  ) {
                    const matchedSub = subData?.find((s) => s.uid === uid);
                    const displayLabel = matchedSub?.hint || uid;
                    items.push({
                      item_name: displayLabel,
                      value: String(val),
                      ...(question.required_for_cdss && {
                        required_for_cdss: true,
                      }),
                      cdss_parameter: matchedSub?.cdss_parameter || null,
                    });
                  }
                });
              });
              return;
            }

            // Original leaf extraction for all other cases
            const leaves = extractLeafValues(relatedQuestions);
            if (leaves.length > 0) {
              finalValueString = leaves.join(", ");

              if (
                relatedQuestions &&
                typeof relatedQuestions === "object" &&
                Object.keys(relatedQuestions).length === 1
              ) {
                const opt = Object.keys(relatedQuestions)[0];
                const val = relatedQuestions[opt];

                if (val === "Yes") {
                  finalValueString = `${opt} : Yes`;
                } else {
                  finalValueString = leaves.join(", ");
                }
              } else {
                finalValueString = leaves.join(", ");
              }
            } else if (hasValue) {
              // Fallback: If related questions exist but yield no leaves (unlikely), use main value
              finalValueString = Array.isArray(value)
                ? value.join(", ")
                : String(value);
            }
            let parentPrefixes = [];
            const valArray = Array.isArray(value)
              ? value
              : value
                ? [value]
                : [];
            valArray.forEach((v) => {
              const valStr =
                typeof v === "object" && v !== null
                  ? Object.keys(v)[0]
                  : String(v);
              if (valStr === "Yes" || valStr === "No") {
                parentPrefixes.push(valStr);
                return;
              }
              const option = [
                ...(question.options || []),
                ...(question.component_data || []),
              ].find(
                (o) =>
                  o.uid === valStr ||
                  o.value === valStr ||
                  o.label === valStr ||
                  o.name === valStr
              );

              if (option) {
                parentPrefixes.push(
                  option.label ||
                    option.title ||
                    option.hint ||
                    option.name ||
                    valStr
                );
              } else {
                parentPrefixes.push(valStr);
              }
            });

            const prefixStr =
              parentPrefixes.length > 0 ? parentPrefixes.join(", ") : "Yes";
            finalValueString = prefixStr + " - " + finalValueString;
          }
          // 3. Fallback: No sub-data, just value (e.g. "Yes" with no sub-selections made)
          else if (hasValue) {
            finalValueString = Array.isArray(value)
              ? value.join(", ")
              : String(value);
          }
          if (finalValueString) {
            items.push({
              item_name: fieldName,
              value: finalValueString,
              ...(question.required_for_cdss && { required_for_cdss: true }),
              cdss_parameter: question.cdss_parameter || null,
            });
          }
          // CRITICAL: Return here to prevent flattening logic from running
          return;
        }

        //Dropdown
        if (isDropDownWithTitleOptionParent && hasValue) {
          const existingItem = items.find((i) => i.item_name === fieldName);

          if (!existingItem) {
            items.push({
              item_name: fieldName,
              value: Array.isArray(value) ? value[0] : String(value), // "Yes"
              ...(question.required_for_cdss && { required_for_cdss: true }),
              cdss_parameter: question.cdss_parameter || null,
            });
          }
        }

        // multiple_component_data parent should ONLY show "Yes"
        const hasSubAnswers =
          relatedQuestions && Object.keys(relatedQuestions).length > 0;
        if (
          (question.schema === "multiple_component_data" ||
            question.schema === "multiple_fields") &&
          (hasValue || hasSubAnswers)
        ) {
          const existingItem = items.find((i) => i.item_name === fieldName);
          if (existingItem) {
            if (question.required_for_cdss)
              existingItem.required_for_cdss = true;
            if (question.cdss_parameter)
              existingItem.cdss_parameter = question.cdss_parameter;
          } else {
            items.push({
              item_name: fieldName,
              value: "Yes",
              ...(question.required_for_cdss && { required_for_cdss: true }),
              cdss_parameter: question.cdss_parameter || null,
            });
          }
        }

        // Fallback to Flattening or Standard Aggregation
        if (hasValue && !isDropDownWithTitleOptionParent) {
          let displayValue = value;

          const resolveObjectValue = (val) => {
            if (
              typeof val === "object" &&
              val !== null &&
              !Array.isArray(val)
            ) {
              const k = Object.keys(val)[0];
              const option = [
                ...(question.options || []),
                ...(question.component_data || []),
              ].find((o) => o.uid === k || o.value === k || o.label === k);
              const label = option?.label || option?.title || option?.hint || k;
              return `${label} : ${val[k]}`;
            }
            return val;
          };

          if (Array.isArray(value)) {
            if (question.multiple_selection) {
              displayValue = value.map(resolveObjectValue).join(", ");
            } else {
              displayValue = resolveObjectValue(value[0]);
            }
          } else {
            displayValue = resolveObjectValue(value);
          }
          // append unit ONLY for input fields
          if (
            question.schema === "input_single_line" ||
            question.schema === "input_multiline"
          ) {
            displayValue = appendUnitIfNeeded(question, displayValue);
          }

          //  SPECIAL CASE: radio + option-based sub-questions
          if (
            question.schema === "radio" &&
            question.multiple_selection &&
            relatedQuestions &&
            Object.keys(relatedQuestions).length > 0
          ) {
            const parts = [];
            Object.entries(relatedQuestions).forEach(
              ([optionLabel, answers]) => {
                if (Array.isArray(answers) && answers.length > 0) {
                  const leaf = answers[0];
                  if (typeof leaf === "object") {
                    const subVal = Object.values(leaf)[0];
                    parts.push(`${optionLabel} : ${String(subVal).trim()}`);
                  } else {
                    parts.push(`${optionLabel} : ${String(leaf).trim()}`);
                  }
                } else if (
                  typeof answers === "string" ||
                  typeof answers === "number"
                ) {
                  // NEW: Handle primitive string values (e.g. preserved "Others" input)
                  parts.push(`${optionLabel} : ${String(answers).trim()}`);
                }
              }
            );

            if (parts.length > 0) {
              displayValue = parts.join(", ");
            }
          }

          if (selectedOptionInfo) {
            displayValue = `${displayValue} : ${selectedOptionInfo}`;
          }

          const questionKey = question.uid || question.name || fieldName;
          const existingItem = items.find(
            (i) => i.__question_key === questionKey
          );
          if (existingItem) {
            if (question.required_for_cdss)
              existingItem.required_for_cdss = true;
            if (question.cdss_parameter)
              existingItem.cdss_parameter = question.cdss_parameter;
          } else {
            items.push({
              item_name: fieldName,
              value: String(displayValue),
              ...(question.required_for_cdss && { required_for_cdss: true }),
              cdss_parameter: question.cdss_parameter || null,
            });
          }
        }

        // Helper to extract primitive value for a specific key from nested structures
        const extractValueForKey = (val, targetKey) => {
          if (val === null || val === undefined || val === "") {
            return null;
          }
          if (typeof val !== "object") {
            return String(val);
          }
          if (Array.isArray(val)) {
            // If it's an array of primitives, join them
            if (
              val.every((item) => typeof item !== "object" || item === null)
            ) {
              return val.map(String).join(", ");
            }
            // If it's an array of objects, find the one matching targetKey
            for (const item of val) {
              if (typeof item === "object" && item !== null) {
                // Check if this object has the targetKey
                if (item.hasOwnProperty(targetKey)) {
                  const innerVal = item[targetKey];
                  if (Array.isArray(innerVal)) {
                    return innerVal.map(String).join(", ");
                  }
                  return String(innerVal);
                }
                // Also check for nested values
                for (const [k, v] of Object.entries(item)) {
                  if (k === targetKey) {
                    if (Array.isArray(v)) {
                      return v.map(String).join(", ");
                    }
                    return String(v);
                  }
                }
              }
            }
            // Fallback: if no match found, extract all primitives with label resolution
            const primitives = [];
            val.forEach((item) => {
              if (typeof item === "object" && item !== null) {
                Object.entries(item).forEach(([k, v]) => {
                  const option = [
                    ...(question.options || []),
                    ...(question.component_data || []),
                  ].find((o) => o.uid === k || o.value === k || o.label === k);
                  const label =
                    option?.label || option?.title || option?.hint || k;

                  if (Array.isArray(v)) {
                    primitives.push(`${label} : ${v.map(String).join(", ")}`);
                  } else if (typeof v !== "object" || v === null) {
                    primitives.push(`${label} : ${String(v)}`);
                  }
                });
              } else if (item !== null && item !== undefined && item !== "") {
                primitives.push(String(item));
              }
            });
            return primitives.length > 0 ? primitives.join(", ") : null;
          }
          // For objects, check if targetKey exists
          if (val.hasOwnProperty(targetKey)) {
            const innerVal = val[targetKey];
            if (Array.isArray(innerVal)) {
              return innerVal.map(String).join(", ");
            }
            return String(innerVal);
          }
          // Fallback: extract all primitive values
          const primitives = [];
          Object.values(val).forEach((v) => {
            if (Array.isArray(v)) {
              primitives.push(...v.map(String));
            } else if (typeof v !== "object" || v === null) {
              primitives.push(String(v));
            }
          });
          return primitives.length > 0 ? primitives.join(", ") : null;
        };

        // Only process relatedQuestions if aggregation was NOT used
        const isInputField =
          question.schema === "input_multiline" ||
          question.schema === "input_single_line";

        const isOptionBasedYesNo =
          question.schema === "radio" &&
          question.multiple_selection &&
          question.capture_options;

        if (
          !isInputField &&
          !isOptionBasedYesNo &&
          relatedQuestions &&
          Object.keys(relatedQuestions).length > 0
        ) {
          Object.entries(relatedQuestions).forEach(([key, subVal]) => {
            // drop_down_with_title_option multi select
            if (
              isDropDownWithTitleOptionParent &&
              key === "Yes" &&
              Array.isArray(subVal) &&
              subVal.every(
                (x) => typeof x === "object" && x !== null && !Array.isArray(x)
              )
            ) {
              // If any sub-question is required_for_cdss, force required on all these rows
              const forceRequired =
                !!question.required_for_cdss ||
                (Array.isArray(question.sub_questions) &&
                  question.sub_questions.some((sq) => sq?.required_for_cdss));

              subVal.forEach((obj) => {
                Object.entries(obj).forEach(([subUid, vals]) => {
                  const sq = (question.sub_questions || []).find(
                    (s) => s?.uid === subUid
                  );

                  const itemName =
                    sq?.label || sq?.item_name || sq?.drop_down_text || subUid;

                  const valueStr = Array.isArray(vals)
                    ? vals.filter(Boolean).map(String).join(", ")
                    : vals !== null && vals !== undefined
                      ? String(vals)
                      : "";

                  if (!valueStr) return;

                  items.push({
                    item_name: itemName,
                    value: valueStr,
                    ...(forceRequired && { required_for_cdss: true }),
                    cdss_parameter: sq?.cdss_parameter || null,
                  });
                });
              });
              return;
            }

            if (key === "undefined" || key === "null") return;

            const subQSchema = question.sub_questions?.find(
              (sq) => sq.label === key || sq.name === key
            );
            const itemName = subQSchema?.label || key;

            if (
              typeof subVal === "object" &&
              subVal !== null &&
              !Array.isArray(subVal)
            ) {
              const flattenObject = (obj, parentSchema) => {
                Object.entries(obj).forEach(([k, v]) => {
                  const currentKeySchema = parentSchema?.sub_questions?.find(
                    (sq) =>
                      sq && (sq.label?.trim() === k || sq.name?.trim() === k)
                  );

                  if (
                    typeof v === "object" &&
                    v !== null &&
                    !Array.isArray(v)
                  ) {
                    const nextContext = currentKeySchema || parentSchema;
                    flattenObject(v, nextContext);
                  } else if (v !== null && v !== "" && v !== undefined) {
                    const finalItemName = currentKeySchema?.label || k;
                    // Use the key-aware extractor
                    let finalValue = extractValueForKey(v, k);

                    if (finalValue) {
                      // Append unit for input fields
                      if (
                        currentKeySchema?.schema === "input_single_line" ||
                        currentKeySchema?.schema === "input_multiline"
                      ) {
                        finalValue = appendUnitIfNeeded(
                          currentKeySchema,
                          finalValue
                        );
                      }

                      const existingItem = items.find(
                        (i) => i.item_name === finalItemName
                      );
                      if (existingItem) {
                        if (currentKeySchema?.required_for_cdss)
                          existingItem.required_for_cdss = true;
                        if (currentKeySchema?.cdss_parameter)
                          existingItem.cdss_parameter =
                            currentKeySchema.cdss_parameter;
                      } else {
                        items.push({
                          item_name: finalItemName,
                          value: finalValue,
                          ...(currentKeySchema?.required_for_cdss && {
                            required_for_cdss: true,
                          }),
                          cdss_parameter:
                            currentKeySchema?.cdss_parameter || null,
                        });
                      }
                    }
                  }
                });
              };
              const recursionContext = subQSchema || question;
              flattenObject({ [itemName]: subVal }, recursionContext);
            } else if (
              subVal !== null &&
              subVal !== "" &&
              subVal !== undefined
            ) {
              // Use the key-aware extractor with the item name
              let finalValue = extractValueForKey(subVal, itemName);

              if (finalValue) {
                // Append unit for input fields
                if (
                  subQSchema?.schema === "input_single_line" ||
                  subQSchema?.schema === "input_multiline"
                ) {
                  finalValue = appendUnitIfNeeded(subQSchema, finalValue);
                }

                const existingItem = items.find(
                  (i) => i.item_name === itemName
                );
                if (existingItem) {
                  if (subQSchema?.required_for_cdss)
                    existingItem.required_for_cdss = true;
                  if (subQSchema?.cdss_parameter)
                    existingItem.cdss_parameter = subQSchema.cdss_parameter;
                  return;
                } else {
                  items.push({
                    item_name: itemName,
                    value: finalValue,
                    ...(subQSchema?.required_for_cdss && {
                      required_for_cdss: true,
                    }),
                    cdss_parameter: subQSchema?.cdss_parameter || null,
                  });
                }
              }
            }
          });
        }
      };

      if (Array.isArray(questionList)) {
        questionList.forEach((q) => processQuestion(q));
      }
      return items;
    };

    if (questions && Array.isArray(questions)) {
      questions.forEach((category) => {
        console.log("$$$", questions);
        const catObj = {
          category_name:
            questions.length === 1
              ? ""
              : normalizeLabel(
                  category.category_label || category.category_name
                ),
          category_item: [],
        };
        let hasData = false;

        if (category.sub_categories && Array.isArray(category.sub_categories)) {
          category.sub_categories.forEach((subCat) => {
            let isDynamic = false;
            let count = 1;

            if (subCat.isdynamic && subCat.dynamicValue) {
              let targetKey = null;
              for (const key of Object.keys(formData)) {
                if (
                  key.toLowerCase().includes(subCat.dynamicValue.toLowerCase())
                ) {
                  targetKey = key;
                  break;
                }
              }

              const nValue = targetKey
                ? formData[targetKey]
                : formData[subCat.dynamicValue];
              const parsedCount = parseInt(nValue, 10);
              if (!isNaN(parsedCount) && parsedCount > 0) {
                isDynamic = true;
                count = parsedCount;
              } else {
                count = 0; // Don't skip processing but do empty iterations
              }
            }

            for (let i = 1; i <= count; i++) {
              let currentSubCat = subCat;

              if (isDynamic) {
                const baseCategoryLabel =
                  category.uid ||
                  category.category_label ||
                  category.category_name;
                const basePrefix = `${baseCategoryLabel}_${i - 1}`;

                const baseName =
                  (subCat.category_label || subCat.generic_data_name)
                    ?.replace("n", "")
                    .replace(/-/g, "")
                    .trim() || "Child";
                const resolvedLabel = `${baseName} - ${i}`;

                currentSubCat = EditTemplateHelpers.assignDynamicTabIds(
                  subCat,
                  basePrefix,
                  resolvedLabel
                );
                currentSubCat.category_label = resolvedLabel;
              }

              const subCategoryLabel = normalizeLabel(
                currentSubCat.category_label || currentSubCat.category_name
              );
              const childQuestions = currentSubCat.questions;

              if (!childQuestions?.length) continue;

              const subCatItems = processQuestionsList(childQuestions);
              if (subCatItems.length > 0) {
                catObj.category_item.push({
                  category_item: subCategoryLabel,
                  item_data: subCatItems,
                });
                hasData = true;
              }
            }
          });
        } else if (category.questions && Array.isArray(category.questions)) {
          const directItems = processQuestionsList(category.questions);
          if (directItems.length > 0) {
            catObj.category_item.push({
              category_item: normalizeLabel(
                category.category_label || category.category_name
              ),
              item_data: directItems,
            });
            hasData = true;
          }
        }

        if (hasData) result.push(catObj);
      });
    }
    return result;
  }

  // Transform api raw data to form data
  static transform_api_raw_data_to_form_data(raw_data, templateName) {
    const formData = {};
    if (!raw_data || !Array.isArray(raw_data)) return formData;

    raw_data.forEach((mainCategoryObj) => {
      const mainCategoryLabel = Object.keys(mainCategoryObj)[0];
      const mainCategoryValues = mainCategoryObj[mainCategoryLabel];
      if (!Array.isArray(mainCategoryValues)) return;

      // Find Schema if templateName is provided
      const mainCategorySchema =
        templateName && EditTemplateCategories[templateName]
          ? EditTemplateCategories[templateName].find(
              (c) => c.category_label === mainCategoryLabel
            )
          : null;

      mainCategoryValues.forEach((subCategoryObj) => {
        const subCategoryLabel = Object.keys(subCategoryObj)[0];
        const subCategoryValues = subCategoryObj[subCategoryLabel];
        if (!Array.isArray(subCategoryValues)) return;
        let subCategorySchema = mainCategorySchema?.sub_categories?.find(
          (s) => s.category_label === subCategoryLabel
        );

        // If not found directly, check if it's a dynamic generated tab
        if (!subCategorySchema && mainCategorySchema?.sub_categories) {
          const dynamicTemplate = mainCategorySchema.sub_categories.find(
            (s) => s.isdynamic
          );
          if (dynamicTemplate) {
            const baseLabel =
              (
                dynamicTemplate.category_label ||
                dynamicTemplate.generic_data_name
              )
                ?.replace("n", "")
                .replace(/-/g, "")
                .trim() || "Child";
            const isLabelMatch = subCategoryLabel.startsWith(baseLabel);
            const isUidMatch =
              mainCategorySchema.uid &&
              subCategoryLabel.startsWith(`${mainCategorySchema.uid}_`);

            if (isLabelMatch || isUidMatch) {
              const match = subCategoryLabel.match(/\d+$/);
              if (match) {
                const num = parseInt(match[0], 10);
                let zeroBasedIndex = num;
                let displayIndex = num;

                if (isLabelMatch) {
                  // "Child - 1" -> match = 1 -> zeroBasedIndex = 0
                  zeroBasedIndex = num - 1;
                  displayIndex = num;
                } else {
                  // "Past Obstetric History_0" -> match = 0 -> zeroBasedIndex = 0
                  zeroBasedIndex = num;
                  displayIndex = num + 1;
                }

                const basePrefix = `${mainCategorySchema.uid || mainCategorySchema.category_label}_${zeroBasedIndex}`;
                const resolvedLabel = `${baseLabel} - ${displayIndex}`;
                subCategorySchema = EditTemplateHelpers.assignDynamicTabIds(
                  dynamicTemplate,
                  basePrefix,
                  resolvedLabel
                );
                subCategorySchema.category_label = resolvedLabel;
              }
            }
          }
        }

        subCategoryValues.forEach((childQuestionData) => {
          const fieldName = Object.keys(childQuestionData)[0];
          let value = childQuestionData[fieldName];

          // Resolved Names for Form Data Keys (UIDs)
          // Prioritize Schema-defined names/UIDs over API labels
          const resolvedSubCategoryName =
            subCategorySchema?.category_name || subCategoryLabel;

          // Schema Lookup for Question
          const questionSchema = subCategorySchema?.questions?.find(
            (q) =>
              q.label?.trim() === fieldName?.trim() ||
              q.name?.trim() === fieldName?.trim() ||
              q.uid?.trim() === fieldName?.trim()
          );

          const resolvedQuestionName =
            questionSchema?.uid || questionSchema?.name || fieldName;

          const isComplexRadio =
            questionSchema?.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.RADIO &&
            Array.isArray(value) &&
            value.length > 0 &&
            typeof value[0] === "object" &&
            value[0] !== null &&
            questionSchema?.sub_questions?.some(
              (sq) =>
                sq.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.RADIO &&
                (!sq.sub_questions || sq.sub_questions.length === 0)
            );
          const isNestedComplexRadio =
            questionSchema?.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.RADIO &&
            Array.isArray(value) &&
            value.length > 0 &&
            typeof value[0] === "object" &&
            value[0] !== null &&
            (
              questionSchema?.sub_questions || questionSchema?._sub_questions
            )?.some(
              (sq) =>
                sq.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.RADIO &&
                (sq.sub_questions || sq._sub_questions)?.some(
                  (subSq) =>
                    subSq.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.RADIO &&
                    (!subSq.sub_questions ||
                      subSq.sub_questions.length === 0) &&
                    (!subSq._sub_questions || subSq._sub_questions.length === 0)
                )
            );

          if (questionSchema?.have_2_option) {
            const mainQuestionKey = EditTemplateHelpers.getQuestionKey(
              resolvedSubCategoryName,
              resolvedQuestionName
            );

            this.prepopulateNestedLevel(
              questionSchema,
              value,
              formData,
              mainQuestionKey
            );
            return;
          }

          if (
            (questionSchema?.schema ===
              EDIT_TEMPLATE_QUESTION_SCHEMA.SELECTION_WITH_QUESTIONS ||
              questionSchema?.schema ===
                EDIT_TEMPLATE_QUESTION_SCHEMA.MULTIPLE_FIELDS ||
              questionSchema?.schema ===
                EDIT_TEMPLATE_QUESTION_SCHEMA.MULTIPLE_COMPONENT_DATA ||
              isComplexRadio) &&
            !isNestedComplexRadio &&
            Array.isArray(value) &&
            questionSchema.component_data &&
            questionSchema.component_data.length > 0
          ) {
            // Initialize parent array if needed (though mapped sub-questions are the primary target)
            const parentKey = EditTemplateHelpers.getQuestionKey(
              resolvedSubCategoryName,
              resolvedQuestionName
            );

            if (!formData[parentKey]) {
              formData[parentKey] = [];
            }

            // Iterate through the component_data (sub-questions) defined in the schema
            if (
              questionSchema.component_data &&
              Array.isArray(questionSchema.component_data)
            ) {
              questionSchema.component_data.forEach((compItem) => {
                // Find matching entry in the raw value
                // Raw value structure: [{ "Question Text": ["Answer"] }, ...]
                const matchingEntry = value.find((valItem) => {
                  if (valItem && typeof valItem === "object") {
                    const key = Object.keys(valItem)[0];
                    // Flexible matching for question text
                    const normalize = (str) => String(str).trim().toLowerCase();
                    const keyNorm = normalize(key);
                    return (
                      (compItem.uid && keyNorm === normalize(compItem.uid)) ||
                      (compItem.name && keyNorm === normalize(compItem.name)) ||
                      (compItem.question &&
                        keyNorm === normalize(compItem.question)) ||
                      (compItem.label &&
                        keyNorm === normalize(compItem.label)) ||
                      (compItem.hint && keyNorm === normalize(compItem.hint))
                    );
                  }
                  return false;
                });

                if (matchingEntry) {
                  const key = Object.keys(matchingEntry)[0];
                  let answers = matchingEntry[key];

                  const subItemName =
                    compItem.uid ||
                    compItem.name ||
                    compItem.question ||
                    compItem.hint ||
                    compItem.label;

                  if (answers) {
                    const isSingleValueType =
                      compItem.type === "input_single_line" ||
                      compItem.type === "input_single_line_with_title" ||
                      compItem.type === "input_multiline" ||
                      compItem.type === "input_multiline_with_title";

                    if (
                      isSingleValueType &&
                      Array.isArray(answers) &&
                      answers.length > 0
                    ) {
                      // Use only the first value if it's a single input type receiving an array
                      answers = answers[0];
                    } else if (
                      isSingleValueType &&
                      Array.isArray(answers) &&
                      answers.length === 0
                    ) {
                      answers = "";
                    }

                    if (
                      answers &&
                      (!Array.isArray(answers) || answers.length > 0)
                    ) {
                      // Validate "Yes" against component data (Load side filter)
                      let isValid = true;
                      const answerStr = Array.isArray(answers)
                        ? answers[0]
                        : answers;

                      if (
                        String(answerStr) === "Yes" &&
                        compItem.component_data &&
                        Array.isArray(compItem.component_data)
                      ) {
                        const hasYesOption = compItem.component_data.some(
                          (opt) => opt.name === "Yes" || opt.uid === "Yes"
                        );
                        if (!hasYesOption) isValid = false;
                      }

                      if (isValid) {
                        if (compItem.input_field) {
                          // 1. Option with input_field
                          // Do NOT recurse — we don't want to create an extra flat key
                          // like copd__cardiorespiratory_6_15__cardiorespiratory_6_15_2 = "3333".
                          // Instead, push an object { uid: "3333" } to the parent array
                          // so RadioWithAutoSelection can extract it for the inline input.
                          if (Array.isArray(formData[parentKey])) {
                            const hasKey = formData[parentKey].some(
                              (item) =>
                                typeof item === "object" &&
                                item !== null &&
                                item[subItemName] !== undefined
                            );
                            if (!hasKey) {
                              formData[parentKey].push({
                                [subItemName]: answers,
                              });
                            }
                          }
                        } else {
                          // 2. Normal options without input_field
                          // Recursively process to handle complex nested data (e.g. Inconclusive -> Otoscopic Exam)
                          this.processRawDataRecursively(
                            answers,
                            [
                              resolvedSubCategoryName,
                              resolvedQuestionName,
                              subItemName,
                            ],
                            formData,
                            compItem
                          );

                          // Only add to parent selection if valid AND meaningful
                          if (
                            Array.isArray(formData[parentKey]) &&
                            !formData[parentKey].includes(subItemName)
                          ) {
                            formData[parentKey].push(subItemName);
                          }
                        }
                      }
                    }
                  }
                }
                // Else case removed to avoid ReferenceError
              });
            } else {
              // Fallback: If component_data is missing, trust the raw data keys]

              value.forEach((itemObj) => {
                if (itemObj && typeof itemObj === "object") {
                  const questionText = Object.keys(itemObj)[0];
                  const answer = itemObj[questionText];

                  // Try to find sub-question definition in sub_questions array if available
                  const subQuestion = questionSchema.sub_questions?.find(
                    (sq) =>
                      sq.label === questionText || sq.name === questionText
                  );

                  if (subQuestion) {
                    formData[subQuestion.name] = answer;
                    if (!formData[parentKey].includes(subQuestion.name)) {
                      formData[parentKey].push(subQuestion.name);
                    }
                  } else {
                    // If no schema found, try constructing a key assuming standard naming convention
                    const subQuestionKey = EditTemplateHelpers.getQuestionKey(
                      resolvedSubCategoryName,
                      resolvedQuestionName,
                      questionText
                    );
                    formData[subQuestionKey] = answer;
                  }
                }
              });
            }
            return; // Handled selection_with_questions
          }

          // Special Handling for LIKERT (Reverse Map Labels to Keys)
          if (
            questionSchema?.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.LIKERT &&
            Array.isArray(value)
          ) {
            const mainKey = questionSchema.name;
            if (!formData[mainKey]) formData[mainKey] = {};

            value.forEach((itemObj) => {
              if (itemObj && typeof itemObj === "object") {
                const label = Object.keys(itemObj)[0];
                const val = itemObj[label];

                if (questionSchema.likert_data) {
                  const normalizedLabel = label.trim();
                  const idx = questionSchema.likert_data.findIndex(
                    (d) => d.question?.trim() === normalizedLabel
                  );

                  if (idx !== -1) {
                    // Construct key: QuestionName_qN matching LikertTable
                    const nestedKey = `${mainKey}_q${idx + 1}`;

                    // Normalize value against schema options if possible
                    let normalizedVal = val;
                    if (questionSchema.likert_options) {
                      const matchedOption = questionSchema.likert_options.find(
                        (opt) => {
                          const optStr = String(opt).trim();
                          const valStr = String(val).trim();
                          if (optStr === valStr) return true;
                          if (optStr === `+${valStr}`) return true; // match "3" to "+3"
                          if (valStr === `+${optStr}`) return true; // match "+3" to "3"
                          if (
                            parseInt(optStr) === parseInt(valStr) &&
                            !isNaN(parseInt(valStr))
                          )
                            return true;
                          return false;
                        }
                      );
                      if (matchedOption) normalizedVal = matchedOption;
                    }
                    formData[mainKey][nestedKey] = normalizedVal;
                  }
                }
              }
            });
            return; // Handled Likert
          }

          // Special handling for have_2_option structures
          if (
            Array.isArray(value) &&
            value.length === 1 &&
            typeof value[0] === "object" &&
            value[0] !== null &&
            Object.keys(value[0]).length === 1 &&
            (Object.keys(value[0])[0] === "Yes" ||
              Object.keys(value[0])[0] === "No" ||
              // Check if key is UID that resolves to any valid option
              (() => {
                const key = Object.keys(value[0])[0];
                const opt =
                  questionSchema?.component_data?.find((c) => c.uid === key) ||
                  questionSchema?.options?.find((o) => o.uid === key);
                return !!opt;
              })())
          ) {
            const rawKey = Object.keys(value[0])[0];
            let wrapperKey = rawKey;

            // Resolve UID -> Name
            const matchingOpt =
              questionSchema?.component_data?.find((c) => c.uid === rawKey) ||
              questionSchema?.options?.find((o) => o.uid === rawKey);
            if (matchingOpt?.name) {
              wrapperKey = matchingOpt.name;
            }

            const innerValue = value[0][rawKey];
            const isNestedStructure =
              Array.isArray(innerValue) &&
              innerValue.length > 0 &&
              innerValue.every(
                (item) =>
                  typeof item === "object" &&
                  item !== null &&
                  Object.keys(item).length === 1
              );
            const isRefactoredStructure =
              typeof innerValue === "object" &&
              innerValue !== null &&
              !Array.isArray(innerValue) &&
              Object.keys(innerValue).length > 0;

            if (isNestedStructure || isRefactoredStructure) {
              const mainQuestionKey = EditTemplateHelpers.getQuestionKey(
                resolvedSubCategoryName,
                resolvedQuestionName
              );
              // Handle complex structure (like Yes with sub-questions) or just simple value assignment
              let recursionPath = [
                resolvedSubCategoryName,
                resolvedQuestionName,
              ];

              // For have_option (but NOT have_2_option), the wrapper (Option UID) is part of the path
              if (!questionSchema?.have_2_option) {
                recursionPath.push(rawKey);
              }

              if (
                wrapperKey === "Yes" ||
                (innerValue &&
                  typeof innerValue === "object" &&
                  Object.keys(innerValue).length > 0)
              ) {
                let selectedBodyParts = [];
                if (innerValue && typeof innerValue === "object") {
                  selectedBodyParts = Array.isArray(innerValue)
                    ? innerValue.map((obj) => Object.keys(obj)[0])
                    : Object.keys(innerValue);
                }

                const initialValue =
                  wrapperKey === "Yes" ? wrapperKey : wrapperKey;
                formData[mainQuestionKey] = [
                  initialValue,
                  ...selectedBodyParts,
                ];

                if (isNestedStructure) {
                  innerValue.forEach((item) =>
                    this.processRawDataRecursively(
                      item,
                      recursionPath,
                      formData
                    )
                  );
                } else {
                  Object.entries(innerValue).forEach(([key, val]) =>
                    this.processRawDataRecursively(
                      val,
                      [...recursionPath, key],
                      formData
                    )
                  );
                }
                value = null;
              } else {
                formData[mainQuestionKey] = wrapperKey;
                value = null;
              }
            }
          }

          if (value !== null) {
            this.processRawDataRecursively(
              value,
              [resolvedSubCategoryName, resolvedQuestionName],
              formData,
              questionSchema
            );
          }
        });
      });
    });

    return formData;
  }

  static processRawDataRecursively(
    value,
    namePath,
    formData,
    parentSchema = null
  ) {
    const mainQuestionKey = EditTemplateHelpers.getQuestionKey(...namePath);

    if (
      Array.isArray(value) &&
      value.length === 1 &&
      typeof value[0] === "object" &&
      value[0] !== null &&
      !Array.isArray(value[0]) &&
      Object.keys(value[0]).length === 1 &&
      Object.keys(value[0])[0] === "Yes"
    ) {
      const innerValue = value[0]["Yes"];

      // Check if this is medicine data (array of objects with medicine_name, dosage, unit)
      const isMedicineData =
        Array.isArray(innerValue) &&
        innerValue.length > 0 &&
        innerValue.every(
          (item) =>
            typeof item === "object" &&
            item !== null &&
            ("medicine_name" in item ||
              "dosage" in item ||
              "unit" in item ||
              "Medicine name" in item ||
              "Strength (per puff)" in item ||
              "Dose (Puffs)" in item ||
              "Frequency" in item ||
              "Route" in item)
        );

      if (isMedicineData) {
        // For medicine data, set parent to "Yes" and store medicine array in sub-question
        formData[mainQuestionKey] = "Yes";

        // Medicine data is stored in a sub-question with "Yes" in the path
        const medicineFieldKey = EditTemplateHelpers.getQuestionKey(
          ...namePath,
          "Yes"
        );
        formData[medicineFieldKey] = innerValue;

        return;
      }

      if (Array.isArray(innerValue)) {
        formData[mainQuestionKey] = "Yes";

        // Sub-values are stored in a field with "Yes" in the path
        const subFieldKey = EditTemplateHelpers.getQuestionKey(
          ...namePath,
          "Yes"
        );
        formData[subFieldKey] = innerValue;

        if (
          innerValue.some((item) => typeof item === "object" && item !== null)
        ) {
          this.processRawDataRecursively(
            innerValue,
            [...namePath, "Yes"],
            formData
          );
        }

        return;
      }
    }

    if (
      Array.isArray(value) &&
      value.length > 0 &&
      value.every(
        (item) =>
          typeof item === "object" &&
          item !== null &&
          !Array.isArray(item) &&
          Object.keys(item).length === 1
      )
    ) {
      // Check if this look like it needs a "Yes" wrapper (Hint pattern)
      const firstItem = value[0];
      const firstKey = Object.keys(firstItem)[0];

      const findSchemaGlobal = (targetKey) => {
        if (!targetKey) return null;
        const normalize = (s) =>
          String(s || "")
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "");
        const target = normalize(targetKey);

        for (const tName in EditTemplateCategories) {
          const categories = EditTemplateCategories[tName];
          const searchList = (list) => {
            for (const item of list) {
              if (
                normalize(item.uid) === target ||
                normalize(item.name) === target ||
                normalize(item.label) === target
              ) {
                return item;
              }
              if (item.sub_questions) {
                const found = searchList(item.sub_questions);
                if (found) return found;
              }
              if (item.component_data) {
                const found = searchList(item.component_data);
                if (found) return found;
              }
            }
            return null;
          };
          const found = searchList(categories);
          if (found) return found;
        }
        return null;
      };

      const currentQuestionKey = namePath[namePath.length - 1]; // This is the parent question key
      const schema = findSchemaGlobal(currentQuestionKey);

      let needsYesWrapper = false;

      if (schema) {
        if (schema.have_option || schema.have_2_option) {
          needsYesWrapper = true;
        } else if (
          schema.type === "drop_down_with_title" ||
          schema.schema === "drop_down_with_title" ||
          schema.type === "drop_down_with_title_option" // These almost always need wrapper
        ) {
          needsYesWrapper = true;
        }
      } else {
        // Fallback Heuristics (only if schema not found)
        console.warn("shema not found");
        const knownHintKeys = [
          "Modified his/her daily activities?",
          "Units",
          "Type",
          "Severity",
          "Duration",
          "Quantity",
          "Dosage",
          "Frequency",
          "Time",
          "Details",
        ];
        needsYesWrapper =
          firstKey === "Yes"
            ? false
            : firstKey.includes("?") ||
              knownHintKeys.some(
                (k) => firstKey.includes(k) || k.includes(firstKey)
              );
      }

      if (needsYesWrapper) {
        // Set the parent question value to "Yes"
        formData[mainQuestionKey] = "Yes";

        // Process each hint-based field with "Yes" inserted into the path
        value.forEach((itemObj) => {
          const hintKey = Object.keys(itemObj)[0];
          const hintValue = itemObj[hintKey];

          // Construct field key with "Yes" in the path
          const fieldKey = EditTemplateHelpers.getQuestionKey(
            ...namePath,
            "Yes",
            hintKey
          );

          formData[fieldKey] = hintValue;
        });

        return; // Done processing this pattern
      }
      // If NOT a hint pattern, fall through to standard object processing below
    }

    if (
      Array.isArray(value) &&
      value.every(
        (item) =>
          typeof item === "object" &&
          item !== null &&
          Object.keys(item).length === 1
      )
    ) {
      const selectedValues = [];
      value.forEach((itemObj) => {
        const selectedItem = Object.keys(itemObj)[0];
        const relatedData = itemObj[selectedItem];
        selectedValues.push(selectedItem);
        console.log("relatedData#", relatedData, selectedItem, value);
        if (
          relatedData !== null &&
          relatedData !== undefined &&
          relatedData !== ""
        ) {
          // Retrieve Schema to find UID for the selected item (e.g. Inconclusive -> UID)
          const findSchemaGlobal = (targetKey) => {
            if (!targetKey) return null;
            const normalize = (s) =>
              String(s || "")
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9]/g, "");
            const target = normalize(targetKey);
            for (const tName in EditTemplateCategories) {
              const categories = EditTemplateCategories[tName];
              const searchList = (list) => {
                for (const item of list) {
                  if (
                    normalize(item.uid) === target ||
                    normalize(item.name) === target ||
                    normalize(item.label) === target
                  ) {
                    return item;
                  }
                  if (item.sub_questions) {
                    const found = searchList(item.sub_questions);
                    if (found) return found;
                  }
                  if (item.component_data) {
                    const found = searchList(item.component_data);
                    if (found) return found;
                  }
                }
                return null;
              };
              const found = searchList(categories);
              if (found) return found;
            }
            return null;
          };

          const currentQuestionKey = namePath[namePath.length - 1];
          const schema = findSchemaGlobal(currentQuestionKey);

          let subPathPart = selectedItem;
          let isInputFieldObj = false;
          let inputFieldUid;
          // Try to find the Option UID using parentSchema first, then fallback to findSchemaGlobal
          const findOptionInSchema = (list) => {
            if (!list || !Array.isArray(list)) return null;
            const compareStr = (str) => (str ? String(str).toLowerCase() : "");
            const searchStr = compareStr(selectedItem);
            for (const opt of list) {
              if (
                compareStr(opt.name) === searchStr ||
                compareStr(opt.label) === searchStr ||
                compareStr(opt.uid) === searchStr ||
                compareStr(opt.value) === searchStr
              ) {
                return opt;
              }
              if (opt.options) {
                const f = findOptionInSchema(opt.options);
                if (f) return f;
              }
              if (opt.component_data) {
                const f = findOptionInSchema(opt.component_data);
                if (f) return f;
              }
              if (opt.sub_questions) {
                const f = findOptionInSchema(opt.sub_questions);
                if (f) return f;
              }
              if (opt.sub_data) {
                const f = findOptionInSchema(opt.sub_data);
                if (f) return f;
              }
            }
            return null;
          };

          // Build combined list from parentSchema + findSchemaGlobal fallback
          let schemaForLookup = parentSchema;
          if (!schemaForLookup) {
            schemaForLookup = schema; // schema from findSchemaGlobal (may be null)
          }

          if (schemaForLookup) {
            const allOptionsToSearch = [
              ...(schemaForLookup.options || []),
              ...(schemaForLookup.component_data || []),
              ...(schemaForLookup.sub_questions || []),
              ...(schemaForLookup.sub_data || []),
            ];

            const matchedOption = findOptionInSchema(allOptionsToSearch);

            if (matchedOption) {
              if (matchedOption.uid) {
                subPathPart = matchedOption.uid;
              }
              if (matchedOption.input_field && matchedOption.input_field.uid) {
                inputFieldUid = matchedOption.input_field.uid;
              }
            }
          }

          if (inputFieldUid) {
            // It's an inline input option — store as { option_uid: input_value } in the parent array
            // so the parent form key becomes [{ General_1_2_19_5: '44' }]
            selectedValues.pop(); // Remove the plain string pushed earlier
            selectedValues.push({ [subPathPart]: relatedData });
          } else {
            // Find the matching sub-question schema to pass along for nested input_field detection
            let nestedSchema = null;
            if (schemaForLookup) {
              const allOptions = [
                ...(schemaForLookup.options || []),
                ...(schemaForLookup.component_data || []),
                ...(schemaForLookup.sub_questions || []),
              ];
              nestedSchema =
                allOptions.find(
                  (opt) =>
                    (opt.uid || "").toLowerCase() ===
                    (subPathPart || "").toLowerCase()
                ) || null;
            }
            // Process the related data with the correct path, passing nested schema
            this.processRawDataRecursively(
              relatedData,
              [...namePath, subPathPart],
              formData,
              nestedSchema
            );
          }
        }
      });
      // Always wrap in array to match expected format
      formData[mainQuestionKey] =
        selectedValues.length === 1 ? [selectedValues[0]] : selectedValues;
    } else if (
      typeof value === "object" &&
      value !== null &&
      !Array.isArray(value)
    ) {
      // Schema-based detection for drop_down_with_title
      // We need to find the schema for the current question path to know its type
      // Using EditTemplateHelpers.findQuestionInSchema is ideal if available, otherwise manual traversal

      let isDropDownWithTitle = false;

      const findSchema = (path) => {
        // path is array of strings: [Category, Question, SubQuestion...]
        // Iterate through all templates
        for (const templateName in EditTemplateCategories) {
          const categories = EditTemplateCategories[templateName];
          // 1. Find Main Category
          const mainCat = categories.find(
            (c) => c.category_label === path[0] || c.category_name === path[0]
          );
          if (!mainCat) continue;

          // 2. Find Question/SubCategory
          let currentContext = mainCat;

          const questionName = path[path.length - 1];

          // Helper to find question recursively in a category
          const findInList = (list) => {
            for (const item of list) {
              if (item.label === questionName || item.name === questionName)
                return item;
              if (item.sub_questions) {
                const found = findInList(item.sub_questions);
                if (found) return found;
              }
              if (item.component_data) {
                // drop_down_with_title uses component_data
                const found = findInList(item.component_data);
                if (found) return found;
              }
            }
            return null;
          };

          let foundQuestion = null;
          if (mainCat.sub_categories) {
            for (const sub of mainCat.sub_categories) {
              if (sub.questions) {
                foundQuestion = findInList(sub.questions);
                if (foundQuestion) break;
              }
            }
          } else if (mainCat.questions) {
            foundQuestion = findInList(mainCat.questions);
          }

          if (foundQuestion) return foundQuestion;
        }
        return null;
      };

      const schema = findSchema(namePath);

      if (schema) {
        if (
          schema.type === "drop_down_with_title" ||
          schema.schema === "drop_down_with_title" ||
          schema.type === "drop_down_with_title_option" ||
          schema.schema === "drop_down_with_title_option" ||
          schema.type === "selection_with_questions" ||
          schema.schema === "selection_with_questions"
        ) {
          isDropDownWithTitle = true;
        }
      }

      // Fallback: Check keys for known patterns if schema lookup failed or didn't match
      if (!isDropDownWithTitle) {
        const keys = Object.keys(value);
        const hasQuestionMark = keys.some((key) => key.includes("?"));
        // Add common keys found in dropdown sub-questions - SAFE LIST ONLY
        const knownKeys = [
          "Units",
          "Quantity",
          "Type",
          "Duration",
          "Severity",
          "Dosage",
          "Frequency",
          "How much",
          "What form",
        ];
        const hasKnownKeys = keys.some((key) =>
          knownKeys.some((known) => key.includes(known))
        );

        if (keys.length > 0 && (hasQuestionMark || hasKnownKeys)) {
          isDropDownWithTitle = true;
        }
      }

      if (isDropDownWithTitle) {
        // Set parent question to "Yes"
        formData[mainQuestionKey] = "Yes";

        // Process each sub-field with "Yes" in the path
        Object.entries(value).forEach(([key, val]) => {
          const fieldKey = EditTemplateHelpers.getQuestionKey(
            ...namePath,
            "Yes",
            key
          );
          formData[fieldKey] = val;
        });
      } else {
        // Normal object processing
        Object.entries(value).forEach(([key, val]) => {
          this.processRawDataRecursively(val, [...namePath, key], formData);
        });
      }
    } else {
      formData[mainQuestionKey] = value;
    }
  }
  //for have_2_option types
  static transformNestedFormQuestionToRaw(question, formData) {
    const questionKey = question.name;
    const rawValue = formData[questionKey];
    if (!rawValue) return null;

    const selections = Array.isArray(rawValue) ? rawValue : [rawValue];

    return selections
      .map((sel) => {
        // Handle potential stringified objects
        if (sel === "[object Object]") return null;

        // Level 1 mapping (e.g., "Foot" -> "General_1_2_0")
        const opt = question.component_data?.find(
          (c) => c.name === sel || c.uid === sel
        );
        const itemKey = opt?.uid || sel;

        // Check for input_field as per requirement: if item have inputfield then it should return {uid: value}
        if (opt?.input_field && opt?.uid && formData) {
          const inputFieldKey = `${questionKey}__${EditTemplateHelpers.normalizeUID(
            opt.uid
          )}__${EditTemplateHelpers.normalizeUID(opt.input_field?.uid)}`;
          const inputFieldValue = formData[inputFieldKey];

          if (
            inputFieldValue !== undefined &&
            inputFieldValue !== null &&
            inputFieldValue !== ""
          ) {
            return { [opt.uid]: inputFieldValue };
          }
        }

        // Look for Level 2 data (e.g., "Site - Options for Foot")
        const level2Key = `${questionKey}__${EditTemplateHelpers.normalizeUID(itemKey)}`;
        const level2Value = formData[level2Key];

        if (
          level2Value !== undefined &&
          level2Value !== null &&
          level2Value !== ""
        ) {
          const level2Selections = Array.isArray(level2Value)
            ? level2Value
            : [level2Value];
          const subResult = {};
          const simpleSelections = [];

          level2Selections.forEach((l2Sel) => {
            // Level 2 mapping (e.g., "Foot - Right" -> "General_1_2_0_0")
            const l2KeyRaw =
              typeof l2Sel === "object" &&
              l2Sel !== null &&
              !Array.isArray(l2Sel)
                ? Object.keys(l2Sel)[0]
                : l2Sel;

            // Level 2 mapping
            const l2Opt = opt?.sub_data?.find(
              (s) => s.name === l2KeyRaw || s.uid === l2KeyRaw
            );
            const l2Key = l2Opt?.uid || l2KeyRaw;

            // Check Level 2 input field
            let l2InputFieldValue = null;
            if (l2Opt?.input_field && l2Opt?.uid && formData) {
              const inputFieldKey = `${level2Key}__${EditTemplateHelpers.normalizeUID(l2Opt.uid)}__${EditTemplateHelpers.normalizeUID(l2Opt.input_field?.uid)}`;
              l2InputFieldValue = formData[inputFieldKey];
            }

            if (
              l2InputFieldValue !== null &&
              l2InputFieldValue !== undefined &&
              l2InputFieldValue !== ""
            ) {
              subResult[l2Key] = l2InputFieldValue;
            } else {
              // Look for Level 3 data (e.g., "Severity")
              const level3Key = `${level2Key}__${EditTemplateHelpers.normalizeUID(l2Key)}`;
              const level3Value = formData[level3Key];

              if (
                level3Value !== undefined &&
                level3Value !== null &&
                level3Value !== ""
              ) {
                // If it has Level 3 (like Severity), nest it
                const level3Selections = Array.isArray(level3Value)
                  ? level3Value
                  : [level3Value];

                // Level 3 parsing (to support input_field at Level 3 too)
                const l3Parsed = level3Selections.map((l3Sel) => {
                  const l3KeyRaw =
                    typeof l3Sel === "object" &&
                    l3Sel !== null &&
                    !Array.isArray(l3Sel)
                      ? Object.keys(l3Sel)[0]
                      : l3Sel;
                  const l3Opt = l2Opt?.sub_data?.find(
                    (s) => s.name === l3KeyRaw || s.uid === l3KeyRaw
                  );

                  if (l3Opt?.input_field && l3Opt?.uid && formData) {
                    const inputFieldKey = `${level3Key}__${EditTemplateHelpers.normalizeUID(l3Opt.uid)}__${EditTemplateHelpers.normalizeUID(l3Opt.input_field?.uid)}`;
                    const inputFieldValue = formData[inputFieldKey];
                    if (
                      inputFieldValue !== undefined &&
                      inputFieldValue !== null &&
                      inputFieldValue !== ""
                    ) {
                      return { [l3Opt.uid]: inputFieldValue };
                    }
                  }
                  // Return value (name) for Level 3 leaf node
                  return l3Opt?.name || l3KeyRaw;
                });

                subResult[l2Key] = l3Parsed;
              } else {
                // If no Level 3 (like Ankle-Right), add to simple list
                simpleSelections.push(l2Opt?.name || l2Key);
              }
            }
          });

          // Logic check:
          // If we only have simple selections, return them directly: { UID: [Parts] }
          if (Object.keys(subResult).length === 0) {
            return { [itemKey]: simpleSelections };
          }

          // If we have mixed or deep data, return as array combining objects and strings
          const finalNestedData = [];
          Object.keys(subResult).forEach((k) => {
            finalNestedData.push({ [k]: subResult[k] });
          });
          if (simpleSelections.length > 0) {
            // Combine simple parts if any
            simpleSelections.forEach((s) => finalNestedData.push(s));
          }

          return { [itemKey]: finalNestedData };
        }

        // If no sub-data available for option its value should be added in raw_data(not uid)
        return opt?.name || sel;
      })
      .filter((item) => item !== null);
  }

  static prepopulateNestedLevel(question, rawValue, formData, questionKey) {
    if (!rawValue) return;

    // Normalize input to array
    const selections = Array.isArray(rawValue) ? rawValue : [rawValue];

    const parseLevel = (dataValue, currentKey, componentSchema) => {
      if (!dataValue) return;
      const dataItems = Array.isArray(dataValue) ? dataValue : [dataValue];

      const currentSelectedValues = [];

      dataItems.forEach((item) => {
        if (typeof item === "string") {
          currentSelectedValues.push(item);
        } else if (typeof item === "object" && item !== null) {
          Object.entries(item).forEach(([uid, val]) => {
            const opt = componentSchema?.find(
              (c) => c.uid === uid || c.name === uid
            );
            const optName = opt?.name || uid;

            if (typeof val === "string" && opt?.input_field) {
              const inputFieldKey = `${currentKey}__${EditTemplateHelpers.normalizeUID(
                uid
              )}__${EditTemplateHelpers.normalizeUID(opt.input_field.uid)}`;

              formData[inputFieldKey] = val;

              currentSelectedValues.push(item);
            } else if (Array.isArray(val)) {
              currentSelectedValues.push(optName); // Add parent name to current level
              const nextKey = `${currentKey}__${EditTemplateHelpers.normalizeUID(
                uid
              )}`;
              const nextSchema = opt?.sub_data || [];
              parseLevel(val, nextKey, nextSchema);
            } else if (typeof val === "object" && val !== null) {
              currentSelectedValues.push(optName);
              const nextKey = `${currentKey}__${EditTemplateHelpers.normalizeUID(
                uid
              )}`;
              const nextSchema = opt?.sub_data || [];
              parseLevel([val], nextKey, nextSchema);
            } else {
              currentSelectedValues.push(optName);
            }
          });
        }
      });

      if (currentSelectedValues.length > 0) {
        formData[currentKey] = currentSelectedValues.filter(
          (v, i, a) => typeof v === "object" || a.indexOf(v) === i
        );
      }
    };

    parseLevel(selections, questionKey, question.component_data);
  }

  // static formatHave2OptionTemplateData(question, formData) {
  //   const baseKey = question.name;
  //   const l1SelectionsRaw = formData[baseKey];
  //   if (!l1SelectionsRaw) return null;
  //   const l1Selections = Array.isArray(l1SelectionsRaw)
  //     ? l1SelectionsRaw
  //     : [l1SelectionsRaw].filter(Boolean);
  //   if (!l1Selections.length) return null;

  //   const isNested = question.is_nested === true;
  //   const lines = [];

  //   const getOptionText = (opt, currentKey) => {
  //     if (opt.input_field) {
  //       const inputFieldKey = `${currentKey}__${EditTemplateHelpers.normalizeUID(
  //         opt.uid
  //       )}__${EditTemplateHelpers.normalizeUID(opt.input_field.uid)}`;
  //       const inputVal = formData[inputFieldKey];
  //       if (inputVal) return inputVal;
  //     }
  //     return opt.name || opt.label || opt.uid;
  //   };

  //   question.component_data?.forEach((l1Opt) => {
  //     const l1IsSelected = l1Selections.some((s) => {
  //       if (typeof s === "object" && s !== null)
  //         return Object.keys(s)[0] === l1Opt.uid;
  //       return s === l1Opt.uid || s === l1Opt.name;
  //     });

  //     const l1Name = getOptionText(l1Opt, baseKey);
  //     const l1HasSubData = l1Opt.sub_data && l1Opt.sub_data.length > 0;

  //     const l2Key = `${baseKey}__${EditTemplateHelpers.normalizeUID(l1Opt.uid)}`;
  //     const l2SelectionsRaw = formData[l2Key];
  //     const l2Selections = Array.isArray(l2SelectionsRaw)
  //       ? l2SelectionsRaw
  //       : [l2SelectionsRaw].filter(Boolean);

  //     if (l1HasSubData && l2Selections.length > 0) {
  //       const l2FormattedParts = [];

  //       l1Opt.sub_data.forEach((l2Opt) => {
  //         const l2IsSelected = l2Selections.some((s) => {
  //           if (typeof s === "object" && s !== null)
  //             return Object.keys(s)[0] === l2Opt.uid;
  //           return s === l2Opt.uid || s === l2Opt.name;
  //         });

  //         if (l2IsSelected) {
  //           let l2Name = getOptionText(l2Opt, l2Key);
  //           // Clean redundant L1 prefixes e.g. "Foot" prefix in "Foot - Right"
  //           const prefixRegex = new RegExp(`^${l1Name}\\s*[-–]\\s*`, "i");
  //           if (isNested && prefixRegex.test(l2Name)) {
  //             l2Name = l2Name.replace(prefixRegex, "");
  //           }

  //           const l2HasSubData = l2Opt.sub_data && l2Opt.sub_data.length > 0;

  //           const l3Key = `${l2Key}__${EditTemplateHelpers.normalizeUID(
  //             l2Opt.uid
  //           )}`;
  //           const l3SelectionsRaw = formData[l3Key];
  //           const l3Selections = Array.isArray(l3SelectionsRaw)
  //             ? l3SelectionsRaw
  //             : [l3SelectionsRaw].filter(Boolean);

  //           if (l2HasSubData && l3Selections.length > 0) {
  //             const l3Names = [];
  //             l2Opt.sub_data.forEach((l3Opt) => {
  //               const l3IsSelected = l3Selections.some((s) => {
  //                 if (typeof s === "object" && s !== null)
  //                   return Object.keys(s)[0] === l3Opt.uid;
  //                 return s === l3Opt.uid || s === l3Opt.name;
  //               });
  //               if (l3IsSelected) {
  //                 l3Names.push(getOptionText(l3Opt, l3Key));
  //               }
  //             });

  //             if (l3Names.length > 0) {
  //               if (isNested) {
  //                 l2FormattedParts.push(`${l2Name} (${l3Names.join(", ")})`);
  //               } else {
  //                 lines.push(`${l2Name} - ${l3Names.join(", ")}`);
  //               }
  //             } else {
  //               if (isNested) l2FormattedParts.push(l2Name);
  //               else lines.push(l2Name);
  //             }
  //           } else {
  //             if (isNested) l2FormattedParts.push(l2Name);
  //             else lines.push(l2Name);
  //           }
  //         }
  //       });

  //       if (isNested && l2FormattedParts.length > 0) {
  //         lines.push(`${l1Name} - ${l2FormattedParts.join(", ")}`);
  //       }
  //     } else if (l1IsSelected) {
  //       lines.push(l1Name);
  //     }
  //   });

  //   if (lines.length > 0) {
  //     return lines;
  //   }

  //   return null;
  // }
  static formatNestedTemplateData(question, formData, isHaveOption = false) {
    const baseKey = question.name;
    const l1SelectionsRaw = formData[baseKey];
    if (!l1SelectionsRaw) return null;

    const l1Selections = Array.isArray(l1SelectionsRaw)
      ? l1SelectionsRaw
      : [l1SelectionsRaw].filter(Boolean);
    if (!l1Selections.length) return null;

    const isNested = question.is_nested === true || isHaveOption;
    const lines = [];

    const getOptionText = (opt, currentKey) => {
      if (opt.input_field) {
        const inputFieldKey = `${currentKey}__${EditTemplateHelpers.normalizeUID(opt.uid)}__${EditTemplateHelpers.normalizeUID(opt.input_field.uid)}`;
        const inputVal = formData[inputFieldKey];
        if (inputVal) return `${opt.name} : ${inputVal}`;
      }
      return opt.name || opt.label || opt.uid;
    };

    question.component_data?.forEach((l1Opt) => {
      const l1IsSelected = l1Selections.some((s) => {
        if (typeof s === "object" && s !== null)
          return Object.keys(s)[0] === l1Opt.uid;
        return s === l1Opt.uid || s === l1Opt.name;
      });

      const l1Name = getOptionText(l1Opt, baseKey);
      const l1HasSubData = l1Opt.sub_data && l1Opt.sub_data.length > 0;
      const l2Key = `${baseKey}__${EditTemplateHelpers.normalizeUID(l1Opt.uid)}`;
      const l2SelectionsRaw = formData[l2Key];
      const l2Selections = Array.isArray(l2SelectionsRaw)
        ? l2SelectionsRaw
        : [l2SelectionsRaw].filter(Boolean);

      if (l1HasSubData && l2Selections.length > 0) {
        const l2FormattedParts = [];

        l1Opt.sub_data.forEach((l2Opt) => {
          const l2IsSelected = l2Selections.some((s) => {
            if (typeof s === "object" && s !== null)
              return Object.keys(s)[0] === l2Opt.uid;
            return s === l2Opt.uid || s === l2Opt.name;
          });

          if (l2IsSelected) {
            let l2Name = getOptionText(l2Opt, l2Key);

            // if (isNested && prefixRegex.test(l2Name)) {
            //   l2Name = l2Name.replace(prefixRegex, "");
            // }

            const connectorIsParentheses =
              question.is_level_two === true || l1Opt.is_level_two === true;
            const subItemShouldGoToParts = isNested || connectorIsParentheses;
            const itemIsNested = isNested || l2Opt.is_nested === true;
            const l2HasSubData = l2Opt.sub_data && l2Opt.sub_data.length > 0;
            const l3Key = `${l2Key}__${EditTemplateHelpers.normalizeUID(l2Opt.uid)}`;
            const l3SelectionsRaw = formData[l3Key];
            const l3Selections = Array.isArray(l3SelectionsRaw)
              ? l3SelectionsRaw
              : [l3SelectionsRaw].filter(Boolean);

            if (l2HasSubData && l3Selections.length > 0) {
              const l3Names = [];

              l2Opt.sub_data.forEach((l3Opt) => {
                const l3IsSelected = l3Selections.some((s) => {
                  if (typeof s === "object" && s !== null)
                    return Object.keys(s)[0] === l3Opt.uid;
                  return s === l3Opt.uid || s === l3Opt.name;
                });
                if (l3IsSelected) {
                  l3Names.push(getOptionText(l3Opt, l3Key));
                }
              });

              if (l3Names.length > 0) {
                if (itemIsNested) {
                  l2FormattedParts.push(`${l2Name} (${l3Names.join(", ")})`);
                } else if (subItemShouldGoToParts) {
                  l2FormattedParts.push(`${l2Name} - ${l3Names.join(", ")}`);
                } else {
                  lines.push(`${l2Name} - ${l3Names.join(", ")}`);
                }
              } else {
                if (subItemShouldGoToParts) l2FormattedParts.push(l2Name);
                else lines.push(l2Name);
              }
            } else {
              if (subItemShouldGoToParts) l2FormattedParts.push(l2Name);
              else lines.push(l2Name);
            }
          }
        });

        const hasNestedBraces = l2FormattedParts.some((part) =>
          part.includes("(")
        );
        const connectorIsParentheses =
          question.is_level_two === true || l1Opt.is_level_two === true;

        if (
          connectorIsParentheses &&
          !hasNestedBraces &&
          l2FormattedParts.length > 0
        ) {
          lines.push(`${l1Name} (${l2FormattedParts.join(", ")})`);
        } else if (
          (isNested || connectorIsParentheses) &&
          l2FormattedParts.length > 0
        ) {
          lines.push(`${l1Name} - ${l2FormattedParts.join(", ")}`);
        }
      } else if (l1IsSelected) {
        lines.push(l1Name);
      }
    });

    if (lines.length === 0) return "";
    if (question.skipListView !== false) {
      return lines.join(", ");
    }
    return lines.map((line, i) => `\n• ${line}`).join("");
  }
}
